import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../../i18n/LanguageContext";
import { S } from "../../data/strings";
import { IMG } from "../../data/content";
import { MaskLines, EASE } from "../../lib/motion";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden bg-burgundy">
      {/* Parallax image */}
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img
          src={IMG.heroRoyal}
          alt="Royal Indian palace interior with a luxury Nikunj ittar bottle, peacock feather and rose petals"
          className="w-full h-full object-cover object-center"
        />
      </motion.div>
      <motion.div
        className="absolute inset-0"
        style={{ opacity: overlayOpacity }}
      >
        <div className="absolute inset-0 bg-burgundy/45" />
        <div className="absolute inset-0 bg-gradient-to-r from-burgundy via-burgundy/85 to-burgundy/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-burgundy via-burgundy/30 to-burgundy/60" />
      </motion.div>

      {/* Content */}
      <motion.div style={{ y: textY }} className="relative z-10 mx-auto max-w-[1400px] w-full px-6 sm:px-10 pt-24 pb-16">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="flex items-center gap-3 mb-7"
          >
            <span className="h-px w-10 bg-gold" />
            <span className="font-cinzel text-[11px] sm:text-xs tracking-luxe uppercase text-goldlight">
              {t(S.hero.badge)}
            </span>
          </motion.div>

          <h1 className="font-serif font-light text-ivory leading-[1.05] tracking-tight text-4xl sm:text-6xl lg:text-[4.75rem]">
            <MaskLines lines={[t(S.hero.l1)]} />
            <MaskLines lines={[t(S.hero.l2)]} lineClassName="italic text-goldlight" />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
            className="mt-8 max-w-xl text-ivory/75 text-sm sm:text-base leading-relaxed"
          >
            {t(S.hero.sub)}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#collections"
              data-testid="hero-explore-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-medium text-burgundy transition-all duration-300 hover:bg-goldlight hover:-translate-y-0.5"
            >
              {t(S.hero.cta1)}
              <span className="transition-transform duration-300 group-hover:translate-x-1">&#8594;</span>
            </a>
            <a
              href="#about"
              data-testid="hero-story-cta"
              className="inline-flex items-center gap-2 rounded-full border border-ivory/40 px-7 py-3.5 text-sm font-medium text-ivory transition-all duration-300 hover:border-goldlight hover:text-goldlight"
            >
              {t(S.hero.cta2)}
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-cinzel text-[10px] tracking-luxe uppercase text-ivory/50">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-gold to-transparent"
        />
      </motion.div>
    </section>
  );
};
